// client/src/components/ui/TimePicker.jsx
import { useState, useRef, useEffect } from 'react'
import { Clock, X } from 'lucide-react'
import clsx from 'clsx'

// Generate half-hour slots from 07:00 to 19:30
const SLOTS = (() => {
  const slots = []
  for (let h = 7; h <= 19; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`)
    if (h < 19) slots.push(`${String(h).padStart(2, '0')}:30`)
  }
  slots.push('19:30')
  return slots
})()

function normaliseTime(raw) {
  if (!raw) return ''
  const trimmed = raw.trim()
  // Accept HH:MM or HH:MMxx — strip anything beyond 5 chars
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed
  return ''
}

export default function TimePicker({
  value,
  onChange,
  placeholder = 'Select a time',
  className = '',
}) {
  const [open,    setOpen]    = useState(false)
  const [draft,   setDraft]   = useState(value || '')
  const containerRef = useRef(null)
  const listRef      = useRef(null)

  // Keep draft in sync when value changes externally
  useEffect(() => { setDraft(value || '') }, [value])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        commitDraft()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, draft]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to selected slot when dropdown opens
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.querySelector('[data-selected="true"]')
    if (el) el.scrollIntoView({ block: 'center' })
  }, [open])

  const commitDraft = () => {
    const normalised = normaliseTime(draft)
    if (normalised) {
      onChange(normalised)
    } else if (!draft.trim()) {
      onChange('')
    }
    // If malformed, revert to last good value
    setDraft(normalised || value || '')
  }

  const handleSelect = (slot) => {
    setDraft(slot)
    onChange(slot)
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setDraft('')
    onChange('')
  }

  const handleDraftChange = (e) => {
    setDraft(e.target.value)
  }

  const handleDraftKeyDown = (e) => {
    if (e.key === 'Enter') { commitDraft(); setOpen(false) }
    if (e.key === 'Escape') { setDraft(value || ''); setOpen(false) }
  }

  const displayLabel = value || ''
  const hasValue     = !!value

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
          'bg-[var(--bg-base)] border focus:outline-none',
          open
            ? 'border-indigo-500'
            : 'border-[var(--border)] hover:border-[var(--border-strong)]',
          hasValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
        )}
      >
        <Clock size={14} className="shrink-0 text-[var(--text-muted)]" />
        <span className="flex-1 text-left">{displayLabel || placeholder}</span>
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={e => e.key === 'Enter' && handleClear(e)}
            className="shrink-0 p-0.5 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)]
                       hover:bg-[var(--bg-overlay)] transition-colors"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1.5 w-44
                        bg-[var(--bg-surface)] border border-[var(--border)]
                        rounded-xl shadow-xl overflow-hidden animate-scale-in">
          {/* Custom time input */}
          <div className="p-2 border-b border-[var(--border)]">
            <input
              type="text"
              value={draft}
              onChange={handleDraftChange}
              onKeyDown={handleDraftKeyDown}
              placeholder="HH:MM"
              maxLength={5}
              className="w-full px-2.5 py-1.5 text-sm bg-[var(--bg-base)]
                         border border-[var(--border)] rounded-lg
                         text-[var(--text-primary)] placeholder-[var(--text-muted)]
                         focus:outline-none focus:border-indigo-500 transition-colors"
              autoFocus
            />
          </div>

          {/* Slots list */}
          <div ref={listRef} className="max-h-52 overflow-y-auto py-1">
            {SLOTS.map(slot => {
              const isSelected = slot === value
              return (
                <button
                  key={slot}
                  type="button"
                  data-selected={isSelected}
                  onClick={() => handleSelect(slot)}
                  className={clsx(
                    'w-full flex items-center px-3 py-2 text-sm transition-colors',
                    isSelected
                      ? 'bg-indigo-600 text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-overlay)] hover:text-[var(--text-primary)]'
                  )}
                >
                  {slot}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
